import { supabase } from "@/integrations/supabase/client";
import { invokeWithRetry } from './common/invokeWithRetry';
import i18n from "@/i18n";

// Feature flags for attendance management edge function
const FEATURE_FLAGS = {
  GET_ATTENDANCE: true,
  GET_ATTENDANCE_BY_BOOKINGS: true,
  CREATE_ATTENDANCE: true,
  UPDATE_ATTENDANCE: true,
  DELETE_ATTENDANCE: true,
};

export interface Attendance {
  id: string;
  booking_id: string;
  attended: boolean;
  gym_id: string;
  rating?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AttendanceInsert {
  booking_id: string;
  attended: boolean;
  gym_id: string;
  rating?: number;
  notes?: string;
}

export interface AttendanceUpdate {
  attended?: boolean;
  rating?: number;
  notes?: string;
}

/**
 * Get attendance records
 */
export async function getAttendance(gym_id: string, booking_id?: string) {
  if (!FEATURE_FLAGS.GET_ATTENDANCE) {
    // Fallback: direct database call
    let query = supabase
      .from("class_attendance")
      .select("*")
      .eq("gym_id", gym_id);

    if (booking_id) query = query.eq("booking_id", booking_id);

    const { data, error } = await query;
    if (error) return { data: null, error };
    return { data: data as Attendance[], error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("attendance-management", {
    action: "get_attendance",
    gym_id,
    booking_id,
  });

  if (authExpired) {
    return { data: [], error: null };
  }

  if (error) {
    console.error("[attendanceWrapper] get_attendance error:", error);
    return { data: null, error };
  }

  return { data: data.attendance as Attendance[], error: null };
}

/**
 * Create attendance record
 */
export async function createAttendance(attendance: AttendanceInsert) {
  if (!FEATURE_FLAGS.CREATE_ATTENDANCE) {
    // Fallback: direct database call
    const { data, error } = await supabase
      .from("class_attendance")
      .insert(attendance)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data as Attendance, error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("attendance-management", {
    action: "create_attendance",
    ...attendance,
  });

  if (authExpired) {
    // `{ data: null, error: null }` franchissait le `if (error)` des appelants :
    // l'écran annonçait « Présence marquée » sans qu'aucune écriture ne parte.
    return { data: null, error: error ?? new Error(i18n.t("common:errors.sessionExpired")) };
  }

  if (error) {
    console.error("[attendanceWrapper] create_attendance error:", error);
    return { data: null, error };
  }

  return { data: data.attendance as Attendance, error: null };
}

/**
 * Update attendance record
 */
export async function updateAttendance(
  attendance_id: string,
  gym_id: string,
  updates: AttendanceUpdate
) {
  if (!FEATURE_FLAGS.UPDATE_ATTENDANCE) {
    // Fallback: direct database call
    const { data, error } = await supabase
      .from("class_attendance")
      .update(updates)
      .eq("id", attendance_id)
      .eq("gym_id", gym_id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data as Attendance, error: null };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("attendance-management", {
    action: "update_attendance",
    attendance_id,
    gym_id,
    updates,
  });

  if (authExpired) {
    // Idem création : la session expirée doit ressortir en erreur.
    return { data: null, error: error ?? new Error(i18n.t("common:errors.sessionExpired")) };
  }

  if (error) {
    console.error("[attendanceWrapper] update_attendance error:", error);
    return { data: null, error };
  }

  return { data: data.attendance as Attendance, error: null };
}

/**
 * Taille de lot pour `booking_ids`.
 *
 * Le `.in("booking_id", …)` qui sert cette action — côté edge function
 * (`attendance-management/index.ts:259`) comme dans le repli direct ci-dessous —
 * est traduit par supabase-js en requête **GET**, donc en chaîne de requête :
 * `booking_id=in.(uuid,uuid,…)`, soit ~37 caractères par identifiant.
 *
 * L'écran Présences ne demandait que les ~20 lignes de la page ; il demande
 * désormais toutes les réservations des journées affichées, jusqu'à 1000
 * (`Attendance.tsx:30`). Envoyés d'un bloc, ces 1000 identifiants produisent une
 * URL d'environ 37 ko, que la passerelle rejette (414 / en-tête trop long) — et
 * `Attendance.tsx:169` transforme cet échec en `setAttendeesData({})` : tous les
 * compteurs « inscrits/capacité » de l'écran tombent à 0. On découpe donc.
 * 200 identifiants ≈ 7,4 ko de chaîne de requête, largement sous les limites
 * usuelles.
 */
const ATTENDANCE_BOOKING_IDS_BATCH_SIZE = 200;

function chunk<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) batches.push(items.slice(i, i + size));
  return batches;
}

/**
 * Get attendance records by booking IDs (optimized for bulk fetching)
 */
export async function getAttendanceByBookings(gym_id: string, booking_ids: string[]) {
  // Le serveur répond 400 sur un tableau vide (`index.ts:253`) : on court-circuite.
  if (!booking_ids?.length) {
    return { data: [] as Attendance[], error: null };
  }

  const batches = chunk(booking_ids, ATTENDANCE_BOOKING_IDS_BATCH_SIZE);
  const merged: Attendance[] = [];

  for (const batch of batches) {
    if (!FEATURE_FLAGS.GET_ATTENDANCE_BY_BOOKINGS) {
      // Fallback: direct database call
      const { data, error } = await supabase
        .from("class_attendance")
        .select("*")
        .eq("gym_id", gym_id)
        .in("booking_id", batch);

      if (error) return { data: null, error };
      merged.push(...((data ?? []) as Attendance[]));
      continue;
    }

    // Edge function call
    const { data, error, authExpired } = await invokeWithRetry("attendance-management", {
      action: "get_attendance_by_bookings",
      gym_id,
      booking_ids: batch,
    });

    if (authExpired) {
      return { data: [] as Attendance[], error: null };
    }

    if (error) {
      console.error("[attendanceWrapper] get_attendance_by_bookings error:", error);
      // Un lot en échec rendrait le résultat partiel — donc des présences
      // affichées « non pointées ». On remonte l'erreur plutôt que de mentir.
      return { data: null, error };
    }

    merged.push(...((data?.attendance ?? []) as Attendance[]));
  }

  return { data: merged, error: null };
}

/**
 * Delete attendance record
 */
export async function deleteAttendance(attendance_id: string, gym_id: string) {
  if (!FEATURE_FLAGS.DELETE_ATTENDANCE) {
    // Fallback: direct database call
    const { error } = await supabase
      .from("class_attendance")
      .delete()
      .eq("id", attendance_id)
      .eq("gym_id", gym_id);

    return { data: error ? null : { success: true }, error };
  }

  // Edge function call
  const { data, error, authExpired } = await invokeWithRetry("attendance-management", {
    action: "delete_attendance",
    attendance_id,
    gym_id,
  });

  if (authExpired) {
    // Annoncer `{ success: true }` sur une session expirée revenait à déclarer
    // une suppression qui n'est jamais partie. `invokeWithRetry` renseigne
    // désormais `error` en parallèle d'`authExpired` : on le propage, comme le
    // fait déjà `paymentsManagementWrapper`.
    return { data: null, error: error ?? new Error(i18n.t("common:errors.sessionExpired")) };
  }

  if (error) {
    console.error("[attendanceWrapper] delete_attendance error:", error);
    return { data: null, error };
  }

  return { data, error: null };
}
